import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, AlertCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function PatientStart() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading", practice: null, error: null });
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/public/kiosk/start", { practice_code: (code || "").toUpperCase() });
        if (cancelled) return;
        try { localStorage.removeItem("auth_token"); } catch (_e) {}
        setState({ status: "ready", practice: data.practice, error: null });
      } catch (err) {
        if (cancelled) return;
        setState({ status: "error", practice: null, error: formatApiErrorDetail(err.response?.data?.detail) || "Praxis-Code ist ungültig." });
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  // Logo authentifiziert als Blob laden (ein <img src> sendet keinen Kiosk-Token → 401).
  useEffect(() => {
    if (!state.practice?.has_logo) { setLogoUrl(null); return; }
    let url = null;
    api.get("/patient/logo", { responseType: "blob" })
      .then((resp) => { url = URL.createObjectURL(resp.data); setLogoUrl(url); })
      .catch(() => setLogoUrl(null));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [state.practice]);

  if (state.status === "loading") {
    return (
      <div className="min-h-screen bg-emerald-950 grid place-items-center px-6 py-10">
        <div className="text-stone-300 text-lg">Wird vorbereitet…</div>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="min-h-screen bg-emerald-950 grid place-items-center px-6 py-10">
        <div data-testid="patient-start-error" className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 md:p-10 text-center">
          <div className="w-12 h-12 rounded-xl bg-orange-100 text-orange-600 grid place-items-center mx-auto mb-5">
            <AlertCircle className="w-6 h-6" />
          </div>
          <h1 className="font-display text-2xl font-semibold text-emerald-900">Code ungültig</h1>
          <p className="text-stone-500 mt-2 mb-6">{state.error}</p>
          <p className="text-xs text-stone-400">Bitte wenden Sie sich an die Praxis-Rezeption.</p>
          <Link to="/login" className="block mt-6 text-xs text-stone-400 hover:underline">Zum Admin-Login</Link>
        </div>
      </div>
    );
  }

  const p = state.practice;

  return (
    <div className="min-h-screen bg-emerald-950 grid place-items-center px-6 py-10">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 md:p-10">
        <div className="flex flex-col items-center text-center">
          {logoUrl ? (
            <img src={logoUrl} alt="Praxis-Logo" data-testid="patient-start-logo"
              className="w-20 h-20 rounded-2xl object-contain bg-stone-50 border border-stone-100 mb-5" />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-emerald-900 text-white grid place-items-center mb-5">
              <ShieldCheck className="w-6 h-6" />
            </div>
          )}
          <p className="text-sm text-stone-500">Willkommen bei</p>
          <h1 data-testid="patient-start-practice-name" className="font-display text-3xl font-semibold text-emerald-900 mt-1">
            {p?.name}
          </h1>
          <p className="text-stone-500 mt-4 max-w-sm">
            Auf der nächsten Seite erfassen Sie Ihre Daten und unterzeichnen die Einwilligung zur Datenverarbeitung gemäß DSGVO.
          </p>
        </div>

        <Button data-testid="patient-start-begin" onClick={() => navigate("/patient", { replace: true })}
          className="mt-8 w-full h-16 text-lg rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
          Einwilligung starten <ArrowRight className="ml-2 w-5 h-5" />
        </Button>

        <p className="mt-5 text-xs text-stone-400 text-center">
          Ihre Eingaben werden ausschließlich in der oben genannten Praxis gespeichert.
        </p>
      </div>
    </div>
  );
}
